// Shared constants/components for rendering sync record tables.
// Used by both SiteSyncRecords.tsx and Properties/PropertySyncRecords.tsx.
// All enums arrive as strings from the API (Newtonsoft StringEnumConverter).
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

export const CATEGORY_LABEL: Record<string, string> = {
  ProviderSync: "Provider Sync",
  ExternalDataImport: "External Import",
  ExternalDataExport: "External Export",
  Reonboard: "Reonboard",
  CommissioningSync: "Commissioning",
};

export const ENTITY_LABEL: Record<string, string> = {
  Lock: "Locks",
  UnitAccessControlGroup: "Unit ACGs",
  AccessControlGroup: "ACGs",
  Credential: "Credentials",
  LockUnitAssociation: "Lock-Unit",
  PhysicalKey: "Fobs",
};

// SiteSyncOperationFlag names, as Newtonsoft writes the flags enum ("Locks, Units").
const SYNC_TYPE_LABEL: Record<string, string> = {
  Locks: "Locks",
  Units: "Units",
  Reonboard: "Reonboard",
  PhysicalKeys: "Fobs",
  Onboarding: "Onboarding",
};

// Bit values, for an API that doesn't know a flag yet and writes the number instead.
const SYNC_TYPE_BITS: [number, string][] = [
  [1, "Locks"],
  [2, "Units"],
  [4, "Reonboard"],
  [8, "PhysicalKeys"],
];

function syncTypeNames(types: string | number | null | undefined): string[] {
  const value = String(types ?? "").trim();
  if (!value) return [];
  if (/^\d+$/.test(value)) {
    const bits = Number(value);
    const names = SYNC_TYPE_BITS.filter(([bit]) => (bits & bit) === bit).map(([, name]) => name);
    // Keep any bits this UI doesn't know, as their number, rather than dropping them.
    const knownBits = SYNC_TYPE_BITS.reduce((mask, [bit]) => mask | bit, 0);
    const unknownBits = bits & ~knownBits;
    if (unknownBits) names.push(String(unknownBits));
    return names.length > 0 ? names : [value];
  }
  return value.split(",").map((name) => name.trim()).filter(Boolean);
}

/** A sync record's operations with friendly names, e.g. "Locks, Units" or "Fobs". */
export function SyncTypesLabel({ types }: { types: string | number | null | undefined }) {
  const names = syncTypeNames(types);
  if (names.length === 0) return <span style={{ color: "gray" }}>—</span>;
  return <>{names.map((name) => SYNC_TYPE_LABEL[name] ?? name).join(", ")}</>;
}

export function statusColor(status: string): "success" | "error" | "warning" | "default" {
  if (status === "Succeeded") return "success";
  if (status === "Error") return "error";
  if (status === "Processing") return "warning";
  return "default";
}

export interface SiteSyncRecordImpact {
  entityType: string;
  addedCount: number;
  updatedCount: number;
  removedCount: number;
  otherCount: number;
  changedItemCount: number;
  changedItemIdsTruncated: boolean;
  /** Items that failed (e.g. fobs that didn't sync). Absent on older APIs. */
  failedCount?: number;
  /**
   * The failed items' ids, at most 100. One per (fob, sister site) for fob syncs, so ids can
   * repeat. Absent on older APIs.
   */
  failedItemIds?: string[] | null;
}

/** "2 failed" in red, with the failed ids (de-duplicated) in a tooltip and for screen readers. */
function FailedSummary({ count, ids }: { count: number; ids: string[] | null | undefined }) {
  const uniqueIds = Array.from(new Set((ids ?? []).filter(Boolean)));
  const failedStyle = { color: "#c62828", fontWeight: 500 };
  if (uniqueIds.length === 0) return <span style={failedStyle}>{count} failed</span>;

  const idList = uniqueIds.join(", ");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A plain-text button so keyboard users can focus it to open the tooltip; the label
            gives screen readers the ids. */}
        <button
          aria-label={`${count} failed: ${idList}`}
          className="cursor-default rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
          style={failedStyle}
          type="button"
        >
          {count} failed
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm">
        Failed: {idList}
      </TooltipContent>
    </Tooltip>
  );
}

export function ImpactSummary({ impacts }: { impacts: SiteSyncRecordImpact[] }) {
  if (!impacts || impacts.length === 0) {
    return <span style={{ color: "gray" }}>—</span>;
  }

  return (
    <div>
      {impacts.map((impact) => {
        const label = ENTITY_LABEL[impact.entityType] ?? impact.entityType;
        const parts: string[] = [];
        if (impact.addedCount > 0) parts.push(`+${impact.addedCount}`);
        if (impact.updatedCount > 0) parts.push(`~${impact.updatedCount}`);
        if (impact.removedCount > 0) parts.push(`-${impact.removedCount}`);
        if (impact.otherCount > 0) parts.push(`?${impact.otherCount}`);
        const failedCount = impact.failedCount ?? 0;
        // "no changes" would read oddly next to a failure count.
        const summary =
          parts.length > 0 ? parts.join(" ") : failedCount > 0 ? "" : "no changes";
        return (
          <div key={impact.entityType} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            <span style={{ fontWeight: 500 }}>{label}:</span>{" "}
            {summary && <span style={{ fontFamily: "monospace" }}>{summary}</span>}
            {failedCount > 0 && (
              <>
                {summary && " "}
                <FailedSummary count={failedCount} ids={impact.failedItemIds} />
              </>
            )}
            {impact.changedItemIdsTruncated && (
              <span style={{ color: "gray" }}> (truncated)</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
